import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Building2, Landmark, Check, Search, Clock, MapPin, Users, Sparkles } from 'lucide-react';

/**
 * Industry picker for the public-company board.
 *
 * Mirrors the "Search for New Signals" modal beside it, because the two boards
 * are siblings and a user should not have to learn a second dialogue. The
 * differences are only where the data differs: there is no location, since these
 * are all US filers by definition, and no credit line, since nothing is fetched
 * — the SEC sweeps already collected everything and this only reads.
 *
 * INDUSTRY IS REQUIRED, ON PURPOSE
 *
 * The board covers 6,292 companies across two dozen industries. Opening it
 * ungated would show whichever companies happened to sort first, which is a
 * worse first impression than asking one question. The counts beside each
 * industry are shown before the choice is made, so nobody picks a bucket holding
 * fourteen companies without knowing it.
 */

// How recently the filing was made. The board's own ceiling is ~13 months, so
// "Any" means that rather than all time.
/**
 * How recently the filing was made.
 *
 * There is no "any time" option because the board never shows one: signals from
 * documents older than the ceiling are excluded everywhere, so labelling the
 * widest choice "Any" promised something that was never on offer. The widest
 * option is labelled with the real limit, which the server supplies.
 */
const FRESHNESS = [
    { value: 30, label: '1 month' },
    { value: 90, label: '3 months' },
    { value: 180, label: '6 months' },
    { value: 365, label: '1 year' },
];

// Listing venue. Most people mean the main boards; OTC is where the shells and
// micro-caps sit, and excluding it removes a lot of noise.
const VENUES = [
    { value: 'NYSE', label: 'NYSE' },
    { value: 'Nasdaq', label: 'Nasdaq' },
    { value: 'OTC', label: 'OTC' },
];


/**
 * How many companies an industry offers for a given direction.
 *
 * `both` is its own stored count, not the sum of the other two — it means
 * companies doing both at once, which is far rarer. Manufacturing has 210
 * hiring and 259 firing but only 47 doing both, so summing would overstate it
 * by a factor of ten.
 */
const countFor = (industry, direction) => industry[direction] || 0;

const PublicSignalFetchModal = ({
    isOpen,
    onClose,
    onFetch,
    industries = [],
    departments = [],
    skills = [],
    locations = [],
    loading = false,
    defaultIndustries = [],
    defaultDepartments = [],
    defaultSkills = [],
    defaultLocations = [],
    defaultDirection = 'hiring',
    defaultFreshDays = 0,
    defaultExchanges = [],
    maxAgeDays = 400
}) => {
    const [selected, setSelected] = useState(defaultIndustries);
    const [selectedDepts, setSelectedDepts] = useState(defaultDepartments);
    const [selectedSkills, setSelectedSkills] = useState(defaultSkills);
    const [selectedPlaces, setSelectedPlaces] = useState(defaultLocations);
    /*
     * Fixed to hiring on the user-facing board.
     *
     * The Signal type chooser is gone: firing, both and unfinished cuts are the
     * outreach dataset and live in the admin panel. `direction` survives as a
     * value because the industry and department counts are per-direction — the
     * picker has to know which number to show — but nothing here can change it.
     */
    const direction = defaultDirection || 'hiring';
    const [query, setQuery] = useState('');
    const maxMonths = Math.round(maxAgeDays / 30.44);
    const [freshDays, setFreshDays] = useState(defaultFreshDays);
    const [exchanges, setExchanges] = useState(defaultExchanges);

    // Re-seed each time it opens so reopening reflects the board's current state
    // rather than whatever was chosen the first time.
    useEffect(() => {
        if (!isOpen) return;
        setSelected(defaultIndustries);
        setSelectedDepts(defaultDepartments);
        setSelectedSkills(defaultSkills);
        setSelectedPlaces(defaultLocations);
        setQuery('');
        setFreshDays(defaultFreshDays);
        setExchanges(defaultExchanges);
    }, [isOpen, defaultIndustries, defaultDepartments, defaultSkills, defaultLocations, defaultDirection, defaultFreshDays, defaultExchanges]);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = q ? industries.filter((i) => i.name.toLowerCase().includes(q)) : industries;
        // Industries with nothing in them sink rather than disappear, so a user
        // looking for one they expected can see it exists and is empty.
        return [...list].sort((a, b) => countFor(b, direction) - countFor(a, direction));
    }, [industries, query, direction]);

    if (!isOpen) return null;

    const togglePlace = (name) => setSelectedPlaces(
        (prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name])
    );

    const toggleSkill = (name) => setSelectedSkills(
        (prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name])
    );

    const toggleDept = (name) => setSelectedDepts(
        (prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name])
    );

    const toggle = (name) =>
        setSelected((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

    return createPortal(
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ background: 'rgba(15,23,42,.5)', backdropFilter: 'blur(3px)' }}
            onClick={onClose}
        >
            <div
                className="relative w-full bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                style={{ maxWidth: 620, maxHeight: '90vh' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Gradient header — same treatment as the signal scan modal */}
                <div
                    className="relative px-7 py-6 shrink-0"
                    style={{ background: 'linear-gradient(135deg, #4F62E8 0%, #3F57E0 55%, #2C3DBD 100%)' }}
                >
                    <button onClick={onClose} className="absolute top-5 right-5 text-white/70 hover:text-white p-1" aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-3.5">
                        <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
                            <Landmark className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">Hiring Signals (US)</h3>
                            <p className="text-[13px] text-white/75 mt-0.5">From SEC filings and earnings calls — no credits used.</p>
                        </div>
                    </div>
                </div>

                <div className="px-7 py-6 space-y-6 overflow-y-auto">
                    {/* Industries */}
                    <div>
                        <label className="flex items-center justify-between text-sm font-semibold text-gray-800 mb-2.5">
                            <span className="flex items-center gap-1.5">
                                <Building2 className="w-4 h-4 text-[#3F57E0]" /> Industries
                            </span>
                            {selected.length > 0 && (
                                <button onClick={() => setSelected([])} className="text-[11px] font-medium text-gray-400 hover:text-gray-600">
                                    Clear {selected.length}
                                </button>
                            )}
                        </label>

                        <div className="relative mb-2.5">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Filter industries…"
                                className="w-full pl-9 pr-3 py-2 text-[13px] bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#3F57E0]/20 focus:border-[#3F57E0]"
                            />
                        </div>

                        <div className="max-h-[220px] overflow-y-auto pr-1 space-y-1.5">
                            {visible.map((ind) => {
                                const active = selected.includes(ind.name);
                                const n = countFor(ind, direction);
                                const empty = n === 0;
                                return (
                                    <button
                                        key={ind.name}
                                        type="button"
                                        disabled={empty}
                                        onClick={() => toggle(ind.name)}
                                        className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl border transition-all ${
                                            empty
                                                ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                                                : active
                                                    ? 'bg-[#F5F7FF] border-[#3F57E0] ring-1 ring-[#3F57E0]/20'
                                                    : 'bg-white border-gray-200 hover:border-[#3F57E0]/50'
                                        }`}
                                    >
                                        <span className="flex items-center gap-2 min-w-0">
                                            <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 border ${
                                                active ? 'bg-[#3F57E0] border-[#3F57E0]' : 'border-gray-300'
                                            }`}>
                                                {active && <Check className="w-3 h-3 text-white" />}
                                            </span>
                                            <span className={`text-[13px] font-medium truncate ${empty ? 'text-gray-300' : 'text-gray-800'}`}>
                                                {ind.name}
                                            </span>
                                        </span>
                                        <span className={`text-[11px] font-bold tabular-nums shrink-0 ml-2 ${
                                            empty ? 'text-gray-300' : direction === 'firing' ? 'text-red-600' : direction === 'both' ? 'text-gray-700' : 'text-emerald-600'
                                        }`}>
                                            {n}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-2">
                            Leave empty to see every industry at once.
                        </p>
                    </div>

                    {/*
                        Department — which team the signal is about.
                        Sits beside Industries because the two answer different
                        halves of one question: an industry is where a company
                        works, a department is where the user would.
                        Only about a third of signals name a team at all — a
                        10-K's headcount line is company-wide — so this narrows
                        hard, and the caption says so rather than leaving the
                        user to conclude the data is thin.
                    */}
                    <div>
                        <label className="flex items-center justify-between text-sm font-semibold text-gray-800 mb-2.5">
                            <span className="flex items-center gap-1.5">
                                <Users className="w-4 h-4 text-[#3F57E0]" /> Department
                            </span>
                            {selectedDepts.length > 0 && (
                                <button onClick={() => setSelectedDepts([])} className="text-[11px] font-medium text-gray-400 hover:text-gray-600">
                                    Clear {selectedDepts.length}
                                </button>
                            )}
                        </label>

                        <div className="flex flex-wrap gap-1.5">
                            {departments.map((dep) => {
                                const active = selectedDepts.includes(dep.name);
                                const n = countFor(dep, direction);
                                const empty = n === 0;
                                return (
                                    <button
                                        key={dep.name}
                                        type="button"
                                        disabled={empty}
                                        onClick={() => toggleDept(dep.name)}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-medium transition-all ${
                                            empty
                                                ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                                                : active
                                                    ? 'bg-[#F5F7FF] border-[#3F57E0] text-[#3F57E0] ring-1 ring-[#3F57E0]/20'
                                                    : 'bg-white border-gray-200 text-gray-700 hover:border-[#3F57E0]/50'
                                        }`}
                                    >
                                        {active && <Check className="w-3 h-3" />}
                                        {dep.name}
                                        <span className={`text-[10.5px] font-bold tabular-nums ${empty ? 'text-gray-300' : 'text-gray-400'}`}>
                                            {n}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-2">
                            Leave empty for any team. Only signals that name a department can
                            be filtered this way — an annual report's headcount covers the
                            whole company, so choosing one narrows the list sharply.
                        </p>
                    </div>

                    {/*
                        Where the company employs people.
                        The least obvious filter here and the most useful: this
                        board is US-LISTED, which says nothing about where the
                        staff are. 55 of these companies employ in India, 33 in
                        Israel, 12 in the Philippines — so a user outside the US
                        is not browsing a foreign market, they are looking at
                        their own local employers filed under a US ticker.
                    */}
                    {locations.length > 0 && (
                        <div>
                            <label className="flex items-center justify-between text-sm font-semibold text-gray-800 mb-2.5">
                                <span className="flex items-center gap-1.5">
                                    <MapPin className="w-4 h-4 text-[#3F57E0]" /> Employs in
                                </span>
                                {selectedPlaces.length > 0 && (
                                    <button onClick={() => setSelectedPlaces([])} className="text-[11px] font-medium text-gray-400 hover:text-gray-600">
                                        Clear {selectedPlaces.length}
                                    </button>
                                )}
                            </label>

                            <div className="flex flex-wrap gap-1.5">
                                {locations.map((p) => {
                                    const active = selectedPlaces.includes(p.name);
                                    return (
                                        <button
                                            key={p.name}
                                            type="button"
                                            onClick={() => togglePlace(p.name)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-medium transition-all ${
                                                active
                                                    ? 'bg-[#F0FDF4] border-emerald-500 text-emerald-800 ring-1 ring-emerald-500/20'
                                                    : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-400'
                                            }`}
                                        >
                                            {active && <Check className="w-3 h-3" />}
                                            {p.name}
                                            <span className="text-[10.5px] font-bold text-gray-400">{p.companies}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-gray-400 mt-2">
                                Leave empty for anywhere. These are US-listed companies, but their
                                staff are not all in the US — this is where they say they actually
                                employ, hire or cut people.
                            </p>
                        </div>
                    )}

                    {/*
                        Skills — what the company said it needs.
                        The rarest filter here by a wide margin: 143 companies of
                        3,561 name a skill, because it comes from executives
                        speaking on calls rather than from filings. Kept because
                        that is also what makes it valuable — a skill named on a
                        Q1 call precedes the job posting by months. The counts sit
                        on every chip so nobody picks a family holding one company
                        without seeing it first.
                    */}
                    {skills.length > 0 && (
                        <div>
                            <label className="flex items-center justify-between text-sm font-semibold text-gray-800 mb-2.5">
                                <span className="flex items-center gap-1.5">
                                    <Sparkles className="w-4 h-4 text-[#3F57E0]" /> Skills named
                                </span>
                                {selectedSkills.length > 0 && (
                                    <button onClick={() => setSelectedSkills([])} className="text-[11px] font-medium text-gray-400 hover:text-gray-600">
                                        Clear {selectedSkills.length}
                                    </button>
                                )}
                            </label>

                            <div className="flex flex-wrap gap-1.5">
                                {skills.map((sk) => {
                                    const active = selectedSkills.includes(sk.name);
                                    return (
                                        <button
                                            key={sk.name}
                                            type="button"
                                            onClick={() => toggleSkill(sk.name)}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-medium transition-all ${
                                                active
                                                    ? 'bg-[#ECFEFF] border-cyan-500 text-cyan-800 ring-1 ring-cyan-500/20'
                                                    : 'bg-white border-gray-200 text-gray-700 hover:border-cyan-400'
                                            }`}
                                        >
                                            {active && <Check className="w-3 h-3" />}
                                            {sk.name}
                                            <span className="text-[10.5px] font-bold text-gray-400">{sk.companies}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[11px] text-gray-400 mt-2">
                                Leave empty for any. Skills come from earnings calls, where an
                                executive names what they are hiring for — months before the job
                                is posted. Only a few hundred companies say it, so this narrows
                                the list a long way.
                            </p>
                        </div>
                    )}

                    {/* Freshness */}
                    <div>
                        <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-2.5">
                            <Clock className="w-4 h-4 text-[#3F57E0]" /> Filed within
                        </label>
                        <div className="grid grid-cols-4 gap-2">
                            {FRESHNESS.map((f) => {
                                const active = freshDays === f.value;
                                return (
                                    <button key={f.value} type="button"
                                        onClick={() => setFreshDays(active ? 0 : f.value)}
                                        className={`h-10 rounded-xl border text-[12.5px] font-bold transition-all ${
                                            active
                                                ? 'bg-[#3F57E0] border-[#3F57E0] text-white shadow-md shadow-[#3F57E0]/25'
                                                : 'bg-white border-gray-200 text-gray-700 hover:border-[#3F57E0]/50 hover:bg-[#F5F7FF]'
                                        }`}>
                                        {f.label}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1.5">
                            Leave empty for any — which still means the last {maxMonths} months,
                            the widest the board goes. Older filings are never shown, because a
                            headcount from two years ago says nothing about hiring now.
                        </p>
                    </div>

                    {/* Listing venue */}
                    <div>
                        <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 mb-2.5">
                            <MapPin className="w-4 h-4 text-[#3F57E0]" /> Listed on
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            {VENUES.map((v) => {
                                const active = exchanges.includes(v.value);
                                return (
                                    <button key={v.value} type="button"
                                        onClick={() => setExchanges((prev) => prev.includes(v.value)
                                            ? prev.filter((x) => x !== v.value)
                                            : [...prev, v.value])}
                                        className={`h-10 rounded-xl border text-[12.5px] font-bold transition-all ${
                                            active
                                                ? 'bg-[#3F57E0] border-[#3F57E0] text-white shadow-md shadow-[#3F57E0]/25'
                                                : 'bg-white border-gray-200 text-gray-700 hover:border-[#3F57E0]/50 hover:bg-[#F5F7FF]'
                                        }`}>
                                        {v.label}
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1.5">
                            Leave empty for all. Excluding OTC removes most shells and micro-caps.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-7 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-3 shrink-0">
                    <p className="text-[12px] text-gray-500">
                        <span className="font-semibold text-gray-700">No credits</span> · read from stored SEC filings
                    </p>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="px-4 py-2.5 text-[13px] font-semibold text-gray-600 hover:text-gray-800">
                            Cancel
                        </button>
                        <button
                            onClick={() => onFetch({ industries: selected, departments: selectedDepts, skills: selectedSkills, locations: selectedPlaces, direction, freshDays, exchanges })}
                            disabled={loading}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#3F57E0] text-white text-[13px] font-bold hover:bg-[#3548c8] disabled:opacity-40 shadow-md shadow-[#3F57E0]/25"
                        >
                            <Landmark className="w-4 h-4" />
                            {/* No count: it was summed from the industry list alone, so it
                                ignored the department, freshness and venue filters and
                                promised a number the next screen would not show. The chips
                                on the board report the real total, under every filter. */}
                            Show signals
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default PublicSignalFetchModal;
